import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { WeavingConsumptionDialog } from "@/pages/WeavingConsumptionDialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

// P17-UAT-15 REV-D1A.2 · Weaving Consumption = REGISTER-FIRST (SO-style).
// "+ New Consumption Run" opens the transaction popup; row View opens it read-only.

const inp = "h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white text-sm focus:outline-none focus:border-[var(--accent)]";
const card = "border border-[var(--border)] rounded-md bg-[var(--bg-1)] p-4";
const fmt = (v, d = 2) => (v === null || v === undefined || v === "" ? "—" : Number(v).toFixed(d));
const RUN_STATUS = {
  DRAFT: "bg-zinc-500/15 text-zinc-300 border-zinc-500/40",
  POSTED: "bg-emerald-500/15 text-emerald-300 border-emerald-400/40",
  REVERSED: "bg-red-500/15 text-red-300 border-red-500/40",
};

export default function WeavingConsumption() {
  const { user } = useAuth();
  const canPost = ["Administrator", "Admin", "Manager", "Supervisor"].includes(user?.role || "");

  const [product, setProduct] = useState("");
  const [status, setStatus] = useState("");
  const [runs, setRuns] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [openRunId, setOpenRunId] = useState(null);
  const [openMode, setOpenMode] = useState("edit");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/weaving-consumption/runs${product ? `?product_code=${encodeURIComponent(product)}` : ""}`);
      let rows = data.runs || [];
      if (status) rows = rows.filter((r) => r.status === status);
      setRuns(rows);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load register"); }
  }, [product, status]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setOpenRunId(null); setOpenMode("edit"); setDialogOpen(true); };
  const openRow = (rid, m) => { setOpenRunId(rid); setOpenMode(m); setDialogOpen(true); };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5" data-testid="weaving-consumption-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white">Weaving Consumption</h1>
          <p className="text-sm text-zinc-500">Consumption Run Register · WO-first · AUTHORISED QA daily weight · WO-wise V2 posting</p>
        </div>
        {canPost && (
          <button data-testid="wc-new-run" onClick={openNew}
            className="h-9 px-4 rounded-sm bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold">+ New Consumption Run</button>
        )}
      </div>

      <div className={card}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase text-zinc-500">Product Code</label>
            <input className={inp} data-testid="wc-filter-product" value={product} onChange={(e) => setProduct(e.target.value)} placeholder="filter by product" />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase text-zinc-500">Status</label>
            <select className={inp} data-testid="wc-filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All</option><option value="DRAFT">Draft</option>
              <option value="POSTED">Posted</option><option value="REVERSED">Reversed</option>
            </select>
          </div>
          <button data-testid="wc-refresh" onClick={load} className="h-9 px-4 rounded-sm border border-[var(--border)] text-zinc-300 text-sm hover:bg-white/5">Refresh</button>
        </div>
      </div>

      <div className={card}>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-[10px] uppercase text-zinc-500 border-b border-[var(--border)]">
              <th className="px-2 py-2 text-left">Run No.</th><th className="px-2 py-2 text-left">Product</th>
              <th className="px-2 py-2 text-left">Prod. Date Range</th><th className="px-2 py-2 text-right">Metres</th>
              <th className="px-2 py-2 text-left">Created By</th><th className="px-2 py-2 text-left">Posted By</th>
              <th className="px-2 py-2 text-center">Status</th><th className="px-2 py-2 text-center">Action</th>
            </tr></thead>
            <tbody data-testid="wc-runs-list">
              {runs.map((r) => (
                <tr key={r.id} className="border-b border-[var(--border)]/40 hover:bg-white/5" data-testid={`wc-run-row-${r.wcr_no}`}>
                  <td className="px-2 py-1.5 font-mono text-white">{r.wcr_no}</td>
                  <td className="px-2 py-1.5 font-mono text-zinc-300">{r.product_code}</td>
                  <td className="px-2 py-1.5 font-mono text-zinc-500">{r.scope_from} → {r.scope_to}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{fmt(r.total_metres, 2)}</td>
                  <td className="px-2 py-1.5 font-mono text-zinc-400">{r.created_by || "—"}</td>
                  <td className="px-2 py-1.5 font-mono text-zinc-400">{r.posted_by || "—"}</td>
                  <td className="px-2 py-1.5 text-center"><span className={`text-[9px] font-bold px-1 py-0.5 rounded-sm border uppercase ${RUN_STATUS[r.status] || ""}`}>{r.status}</span></td>
                  <td className="px-2 py-1.5 text-center">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button data-testid={`wc-actions-${r.wcr_no}`}
                          className="h-7 px-3 rounded-sm border border-[var(--border)] text-zinc-300 text-[11px] hover:bg-white/10">Actions ▾</button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-[var(--bg-1)] border-[var(--border)] text-sm">
                        <DropdownMenuItem data-testid={`wc-action-view-${r.wcr_no}`} onClick={() => openRow(r.id, "view")}>View</DropdownMenuItem>
                        {r.status === "DRAFT" && canPost && (
                          <DropdownMenuItem data-testid={`wc-action-continue-${r.wcr_no}`} onClick={() => openRow(r.id, "edit")}>Continue</DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
              {runs.length === 0 && <tr><td colSpan={8} className="px-2 py-6 text-center text-zinc-600">No consumption runs — use “+ New Consumption Run”.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <WeavingConsumptionDialog open={dialogOpen} runId={openRunId} mode={openMode} canPost={canPost}
        onClose={() => setDialogOpen(false)} onChanged={load} />
    </div>
  );
}
